import React, { useState, useEffect, useRef, useCallback } from "react";
import { Grid, Button, Icon, Dropdown, Label, Checkbox, Divider } from "semantic-ui-react";
import Joi from "joi";
import { isEmpty, map, trim, isEqual, get, flatten, find, filter, includes, isArray, sortBy, concat } from "lodash";
import PropTypes from "prop-types";
import TextAreaInput from "../../../../Components/TextArea";

// Components
import IKModal from "@Components/IkModal";
import Text from "@Components/Text";
import Input from "@Components/Input";
import FileManager from "@Components/FileManager";
import UploadFile from "@Components/uploadFile";
import StageOptionsComponent from "@Features/ChatsBots/components";
import MenuHistory from "@Features/ChatsBots/components/MenuHistory";
import PrintRender from "@Components/PrintRender";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";

// Redux
import genericAction from "@Actions/generic";
import { connect } from "react-redux";

// Styles
import styles from "./styles.module.css";

const ChatBotCrud = ({ tableValue, modalCrud, onClose, apiData, dispatch, companyName, userName, onAddSuccess, groups }) => {
	const validateFileSize = (base64String, maxSizeMB = 5) => {
		if (!base64String) return true;

		const stringLength = base64String.length;
		const sizeInBytes = 4 * Math.ceil(stringLength / 3) * 0.75;
		const sizeInMB = sizeInBytes / (1024 * 1024);

		return sizeInMB <= maxSizeMB;
	};

	const schema = Joi.object().keys({
		name: Joi.string().min(4).messages({
			"string.min": "Nombre debe tener mínimo 4 caracteres",
			"string.empty": "Nombre es un campo requerido",
		}),
		botWarning: Joi.number().integer().required().messages({
			"any.required": "Tiempo de Aviso es requerido",
			"any.empty": "Tiempo de Aviso es requerido",
			"number.base": "Tiempo de Aviso es requerido",
			"number.integer": "Tiempo de Aviso es requerido",
		}),

		botTimeout: Joi.number().integer().empty(["", null]).required().messages({
			"any.required": "Tiempo de Timeout es requerido",
			"number.base": "Tiempo de Timeout debe ser numérico",
			"number.integer": "Tiempo de Timeout debe ser numérico",
		}),

		botTimeoutAction: Joi.alternatives()
			.try(Joi.string().min(1), Joi.number(), Joi.object())
			.when("botTimeout", {
				is: Joi.number().greater(0),
				then: Joi.required(),
				otherwise: Joi.optional(),
			})
			.messages({
				"string.empty": "Acción de Timeout es requerida cuando se especifica un tiempo de timeout",
				"any.required": "Acción de Timeout es requerida cuando se especifica un tiempo de timeout",
			}),
	});

	const schemaStage = Joi.object().keys({
		type: Joi.string().allow("").optional(),
		name: Joi.string()
			.min(1)
			.when("type", {
				is: "Button",
				then: Joi.string().max(20).messages({
					"string.max": "El Nombre para este Tipo no puede exceder de 20 caracteres",
				}),
				otherwise: Joi.string().max(24).messages({
					"string.max": "El nombre de la etapa no puede exceder 24 caracteres",
				}),
			})
			.required()
			.messages({
				"string.min": "Nombre de la etapa es requerido",
				"string.empty": "Nombre de la etapa es requerido",
			}),
		text: Joi.string().max(1024).required().messages({
			"string.base": "Mensaje es un campo requerido",
			"string.max": "El mensaje no puede exceder 1024 caracteres",
			"string.empty": "Mensaje es un campo requerido",
		}),
		image: Joi.array().items(Joi.any()).messages({
			"array.base": "El campo 'image' es requerido",
		}),
		options: Joi.array().when("type", {
			is: Joi.valid("buttonUri", "buttonPhone"),
			then: Joi.array().optional(),
			otherwise: Joi.array().min(1).required().messages({
				"array.min": "La etapa debe tener al menos 1 opción",
				"array.base": "La etapa debe tener al menos 1 opción",
			}),
		}),
		isActive: Joi.boolean().default(true),
		buttonLabel: Joi.string().max(20).allow("").messages({
			"string.max": "La etiqueta del botón no puede exceder 20 caracteres",
		}),
		buttonUri: Joi.string().allow(""),
		header: Joi.string().max(60).allow("").messages({
			"string.max": "El encabezado no puede exceder 60 caracteres",
		}),
		footer: Joi.string().max(60).allow("").messages({
			"string.max": "El pie de página no puede exceder 60 caracteres",
		}),
	});

	const optionSchema = Joi.object({
		label: Joi.string().max(24).required().messages({
			"string.base": "Nombre es un campo requerido",
			"string.max": "El nombre no puede exceder 24 caracteres",
			"string.empty": "Nombre es un campo requerido",
		}),
		description: Joi.string().max(72).allow("").messages({
			"string.max": "La descripción no puede exceder 72 caracteres",
		}),
		action: Joi.object({
			name: Joi.string().required(),
			type: Joi.string().required(),
		})
			.unknown(true)
			.required()
			.messages({
				"any.required": "Acción es un campo requerido",
				"object.base": "Acción es un campo requerido",
			}),
		document: Joi.string()
			.allow("", null)
			.custom((value, helpers) => {
				if (value && !validateFileSize(value, 5)) {
					return helpers.error("any.invalid");
				}
				return value;
			}, "video-size-validation")
			.messages({
				"any.invalid": "El video no puede ser mayor a 5MB",
			}),
		isActive: Joi.boolean().default(true),
	});

	// State
	const [open, setOpen] = useState(false);
	const [initialFormData, setInitialFormData] = useState([]);
	// const [state, setState] = useState(true);
	const [modalOption, setModalOption] = useState();
	const [stateHeader, setStateHeader] = useState("infobasic");
	const [note, setNote] = useState("");
	const [isExp, setIsExp] = useState(false);
	const [name, setName] = useState("");
	const [isActive, setIsActive] = useState(true);
	const [stages, setStages] = useState([]);
	const [printAction, setPrintAction] = useState(false);
	// const [printTab, setPrintTab] = useState("infobasic");
	const [expandedMenus, setExpandedMenus] = useState({});
	const [expandedOptions, setExpandedOptions] = useState({});

	const [images, setImages] = useState([]);

	// Times
	const [botWarning, setBotWarning] = useState("");
	const [botTimeout, setBotTimeout] = useState("");
	const [botTimeoutAction, setBotTimeoutAction] = useState("");
	const [timeoutActions, setTimeoutActions] = useState([]);
	const [selectedTimeoutActionFields, setSelectedTimeoutActionFields] = useState([]);
	const [timeoutActionValues, setTimeoutActionValues] = useState({});

	// gets
	const [actions, setActions] = useState({});

	// Vidos and Files
	const [document, setDocument] = useState(null);

	// Refs
	const printContentRef = useRef();
	const printAreaRef = useRef();

	// Errors
	const [errors, setErrors] = useState({
		name: "",
		isActive: "",
		botWarning: "",
		botTimeout: "",
		botTimeoutAction: "",
	});

	useEffect(() => {
		setModalOption(modalCrud);
		let isMounted = true;

		(async () => {
			const actionsResp = await dispatch(genericAction({ method: "get", route: "actions/all" }));
			const actionsArr = isArray(actionsResp?.data) ? actionsResp.data : isArray(actionsResp) ? actionsResp : [];
			if (!isMounted) return;

			setActions(actionsArr);

			// Opciones de timeout
			const timeoutOptions = actionsArr
				.filter((action) => {
					return action.function === "closeChat" || action.function === "startChat";
				})
				.map((action) => {
					return {
						key: action.id,
						text: action.name,
						value: action.id,
						function: action.function,
					};
				});
			setTimeoutActions(timeoutOptions);

			// Si está en modo Edit o Watch, hacer GET por ID
			let botData = null;
			if ((modalCrud === "Edit" || modalCrud === "Watch") && tableValue?.id) {
				const freshData = await dispatch(genericAction({ method: "get", route: `bots/${tableValue.id}` }));
				botData = freshData && !isEmpty(freshData) ? freshData : tableValue;
			} else if (modalCrud === "Add") {
				botData = null;
			} else {
				botData = tableValue;
			}

			if (!isMounted) return;

			// Si no hay datos, inicializar vacío
			if (!botData) {
				setName("");
				setIsActive(true);
				setNote("");
				setBotWarning("");
				setBotTimeout("");
				setBotTimeoutAction("");
				setStages([]);
				setInitialFormData({ name: "", isActive: true, note: "", stages: [], botTimeout: "", botWarning: "", botTimeoutAction: "" });
				setStateHeader("infobasic");
				return;
			}

			// Configurar timeout action si existe
			if (botData.botTimeoutAction) {
				const timeoutAction = find(actionsArr, { id: botData.botTimeoutAction.id });
				if (timeoutAction) {
					setSelectedTimeoutActionFields(timeoutAction.fields || []);
					if (botData.botTimeoutAction.fields) {
						const fieldValues = {};
						botData.botTimeoutAction.fields.forEach((field) => {
							fieldValues[field.name] = field.value;
						});
						setTimeoutActionValues(fieldValues);
					}
				}
			}

			const rawStages = sortBy(botData.stages || [], "order");
			const validStageTypes = ["Text", "Image", "Button", "buttonUri", "buttonPhone", "buttonFlow", "List"];
			const mappedStages = rawStages.map((stage) => {
				const sortedOptions = sortBy(stage.options || [], "order");
				const stageUri = isArray(stage.uri) ? stage.uri : stage.uri ? [stage.uri] : [];
				const hasImage = stageUri.length > 0;
				const resolvedType = validStageTypes.includes(stage.type) ? stage.type : hasImage ? "Image" : "Text";

				return {
					...stage,
					uri: stageUri,
					type: resolvedType,
					botWarning: stage.botWarning ?? botData.botWarning ?? null,
					botTimeout: stage.botTimeout ?? botData.botTimeout ?? null,
					isActive: stage.isActive ?? true,
					buttonLabel: stage.buttonLabel ?? "",
					buttonUri: stage.buttonUri ?? "",
					header: stage.header ?? "",
					footer: stage.footer ?? "",
					options: sortedOptions.map((opt, idx) => {
						// La acción ya viene completa del backend
						const { action, id, name: optName, description: optDesc, order: optOrder, isActive: optIsActive } = opt;
						return {
							id,
							label: optName || "",
							description: optDesc || "",
							order: optOrder ?? idx + 1,
							action,
							isActive: optIsActive ?? true,
							stageId:
								action?.function === "moveToMenu"
									? action?.fields?.find((f) => {
											return f.name === "stageId";
									  })?.value ||
									  action?.value ||
									  (opt.stageId !== stage.id ? opt.stageId : null)
									: null,
						};
					}),
				};
			});

			// Actualizar estados
			setName(botData.name || "");
			setIsActive(botData.isActive ?? true);
			setNote(botData.note || "");
			setBotWarning(botData.botWarning ?? "");
			setBotTimeout(botData.botTimeout ?? "");
			setBotTimeoutAction(botData.botTimeoutAction?.id || "");
			setStages(mappedStages);
			setImages(mappedStages);
			setInitialFormData({
				name: botData.name || "",
				isActive: botData.isActive ?? true,
				note: botData.note || "",
				stages: mappedStages,
				botTimeout: botData.botTimeout ?? "",
				botWarning: botData.botWarning ?? "",
				botTimeoutAction: botData.botTimeoutAction?.id || "",
			});

			setStateHeader(modalCrud !== "Add" && mappedStages.length > 0 ? "stages" : "infobasic");
		})();

		return () => {
			isMounted = false;
		};
	}, [tableValue, modalCrud, dispatch]);

	const handlerExpandAll = () => {
		const nextIsExp = !isExp;

		setStages((prev) => {
			return prev.map((stage) => {
				return {
					...stage,
					isOpen: nextIsExp,
					options: (stage.options || []).map((opt) => {
						return { ...opt, isOpen: nextIsExp };
					}),
				};
			});
		});
		setIsExp(nextIsExp);
	};

	const stageValidation = () => {
		const mapStages = map(stages, (stage) => {
			const validation = schemaStage.validate(
				{
					name: stage.name,
					type: stage.type,
					text: stage.text,
					image: stage.image,
					options: stage.options,
					isActive: stage.isActive,
					buttonLabel: stage.buttonLabel,
					buttonUri: stage.buttonUri,
					header: stage.header,
					footer: stage.footer,
				},
				{ abortEarly: false }
			);
			if (validation.error) {
				const { details } = validation.error;
				let errorsVal = {
					nametyping: "",
					nombretypingRepet: "",
					stages: "",
				};
				map(details, (item) => {
					errorsVal = { ...errorsVal, [item.context.key]: item.message };
				});

				return {
					id: stage.id,
					error: errorsVal,
				};
			}
			return null;
		});

		return mapStages;
	};

	const optionValidation = () => {
		const mapOptions = map(stages, (stage) => {
			if (["buttonUri", "buttonPhone"].includes(stage.type)) return null;
			return map(stage.options, (option) => {
				const validation = optionSchema.validate(
					{
						label: option.label,
						action: option.action,
						description: option.description,
						document: isArray(option?.action?.value?.uri) ? option.action.value.uri[0] || null : option?.action?.value?.uri || null,
						isActive: option.isActive,
					},
					{ abortEarly: false }
				);
				if (validation.error) {
					const { details } = validation.error;
					let errorsVal = {
						label: "",
						orden: "",
						action: "",
						description: "",
						document: "",
					};
					map(details, (item) => {
						errorsVal = { ...errorsVal, [item.context.key]: item.message };
					});

					return {
						optionId: option.id,
						stageId: stage.id,
						error: errorsVal,
					};
				}
				return null;
			});
		});

		return flatten(mapOptions);
	};

	const validationGeneral = () => {
		const validation = schema.validate(
			{
				name,
				botWarning,
				botTimeout,
				botTimeoutAction: botTimeoutAction ? String(botTimeoutAction) : botTimeoutAction,
			},
			{ abortEarly: false }
		);
		if (validation.error) {
			const { details } = validation.error;
			let errorsVal = {
				name: "",
				botTimeout: "",
				botWarning: "",
				botTimeoutAction: "",
			};
			map(details, (item) => {
				errorsVal = { ...errorsVal, [item.context.key]: item.message };
			});

			return errorsVal;
		}

		return null;
	};

	// Funciones auxiliares para handlerSaved
	const numOrNull = (v) => {
		return v === "" || v == null ? null : Number(v);
	};

	const processOption = (opt, idx, originalStage, parentStageId) => {
		const originalOption = find(originalStage?.options || [], (o) => {
			return o.id === opt.id;
		});
		const nextOrder = idx + 1;
		const isMoveToMenu = opt?.action?.function === "moveToMenu" && opt?.stageId != null;
		const description = opt.description || "";
		const isActiveOption = opt.isActive ?? true;
		let action = opt.action || null;

		if (isMoveToMenu && action && action.fields) {
			action = {
				...action,
				fields: action.fields.map((f) => {
					if (f.name === "stageId") {
						return { ...f, value: opt.stageId };
					}
					return f;
				}),
			};
		}

		if (!originalOption) {
			return {
				order: nextOrder,
				name: opt.label,
				description,
				action,
				isActive: isActiveOption,
				stageId: parentStageId,
			};
		}

		const currentFields = action?.fields || [];
		const originalFields = originalOption?.action?.fields || [];
		const fieldsChanged = !isEqual(
			currentFields.map((f) => {
				return { name: f.name, value: f.value };
			}),
			originalFields.map((f) => {
				return { name: f.name, value: f.value };
			})
		);

		// Detectar cambios generales
		const hasChanges =
			originalOption.order !== nextOrder ||
			originalOption.label !== opt.label ||
			originalOption.description !== description ||
			(originalOption.isActive ?? true) !== isActiveOption ||
			!isEqual(action.name, originalOption.action?.name) ||
			!isEqual(action.type, originalOption.action?.type) ||
			fieldsChanged;

		if (!hasChanges) return null;

		return {
			id: originalOption.id,
			order: nextOrder,
			name: opt.label,
			description,
			action,
			isActive: isActiveOption,
			stageId: parentStageId,
		};
	};

	const processStage = (stage, indexStage, botWarningValue, botTimeoutValue) => {
		const originalStage = find(initialFormData.stages || [], (s) => {
			return s.id === stage.id;
		});

		// Valores calculados
		const nextOrder = indexStage + 1;
		const stageUri = stage.uri || [];
		const validTypes = ["Text", "Image", "Button", "buttonUri", "buttonPhone", "buttonFlow", "List"];
		const stageType = validTypes.includes(stage.type) ? stage.type : stageUri.length > 0 ? "Image" : "Text";
		const stageBotWarning = stage.botWarning ?? botWarningValue;
		const stageBotTimeout = stage.botTimeout ?? botTimeoutValue;
		const stageIsActive = stage.isActive ?? true;
		const stageButtonLabel = stage.buttonLabel ?? "";
		const stageButtonUri = stage.buttonUri ?? "";
		const stageHeader = stage.header ?? "";
		const stageFooter = stage.footer ?? "";

		const isNoOptionsType = ["buttonUri", "buttonPhone"].includes(stage.type);

		let mappedOptions = [];
		let deletedOptionIds = [];

		if (!isNoOptionsType) {
			const sortedOpts = sortBy(stage.options || [], "order");
			mappedOptions = filter(
				sortedOpts.map((opt, idx) => {
					return processOption(opt, idx, originalStage, stage.id);
				}),
				Boolean
			);

			const originalOptionIds = map(originalStage?.options || [], "id");
			const currentOptionIds = map(stage.options || [], "id");
			deletedOptionIds = filter(originalOptionIds, (id) => {
				return !includes(currentOptionIds, id);
			});
		} else {
			const originalOptionIds = map(originalStage?.options || [], "id");
			deletedOptionIds = originalOptionIds;
		}

		// Detectar cambios en el stage
		const stageChanges = {};
		const isNew = !originalStage;

		if (isNew || originalStage.order !== nextOrder) stageChanges.order = nextOrder;
		if (isNew || stage.name !== originalStage.name) stageChanges.name = stage.name;
		if (isNew || stage.text !== originalStage.text) stageChanges.text = stage.text;
		if (isNew || stageType !== (originalStage.type || "Text")) stageChanges.type = stageType;
		if (!isEqual(stageUri, originalStage?.uri || [])) stageChanges.uri = stageUri;
		if (isNew || stageBotWarning !== originalStage.botWarning) stageChanges.botWarning = stageBotWarning;
		if (isNew || stageBotTimeout !== originalStage.botTimeout) stageChanges.botTimeout = stageBotTimeout;
		if (isNew || (originalStage.isActive ?? true) !== stageIsActive) stageChanges.isActive = stageIsActive;
		if (isNew || (originalStage.buttonLabel ?? "") !== stageButtonLabel) stageChanges.buttonLabel = stageButtonLabel;
		if (isNew || (originalStage.buttonUri ?? "") !== stageButtonUri) stageChanges.buttonUri = stageButtonUri;
		if (isNew || (originalStage.header ?? "") !== stageHeader) stageChanges.header = stageHeader;
		if (isNew || (originalStage.footer ?? "") !== stageFooter) stageChanges.footer = stageFooter;

		if (!isEmpty(mappedOptions)) stageChanges.options = mappedOptions;
		if (!isEmpty(deletedOptionIds)) stageChanges.optionsD = deletedOptionIds;

		if (isEmpty(stageChanges)) return null;
		if (originalStage?.id) stageChanges.id = originalStage.id;

		return stageChanges;
	};

	const handlerSaved = async () => {
		// Validaciones
		const stagesError = stageValidation();
		const optionError = optionValidation();
		const generalError = validationGeneral();

		const hasErrors =
			generalError ||
			stagesError.some((v) => {
				return v !== null;
			}) ||
			optionError.some((v) => {
				return v !== null;
			});

		if (hasErrors) {
			setErrors({ ...generalError, stagesError, optionError });
			return;
		}

		const botWarningValue = numOrNull(botWarning);
		const botTimeoutValue = numOrNull(botTimeout);
		const trimmedName = trim(name);
		const trimmedNote = trim(note);

		// Modo "Add": Crear nuevo bot
		if (modalOption === "Add") {
			const fullTimeoutAction = find(actions, { id: botTimeoutAction });
			const payload = {
				name: trimmedName,
				isActive,
				note: trimmedNote,
				botTimeout: botTimeoutValue,
				botWarning: botWarningValue,
				botTimeoutAction: fullTimeoutAction
					? {
							...fullTimeoutAction,
							fields:
								fullTimeoutAction.fields?.map((field) => {
									let value = timeoutActionValues[field.name] ?? field.value;
									if (field.type === "file" && Array.isArray(value) && value.length === 0) {
										value = null;
									}
									return {
										...field,
										value,
									};
								}) || [],
					  }
					: null,
			};

			const created = await dispatch(genericAction({ method: "post", route: "bots", body: payload }));
			if (created?.id) {
				onAddSuccess?.(created);
				setModalOption("Edit");
			}
			return;
		}

		// Modo "Edit": Detectar cambios
		const formattedStages = filter(
			stages.map((stage, indexStage) => {
				return processStage(stage, indexStage, botWarningValue, botTimeoutValue);
			}),
			Boolean
		);

		const deletedStageIds = filter(map(initialFormData.stages || [], "id"), (id) => {
			return !stages.find((s) => {
				return s.id === id;
			});
		});

		// Construir body con cambios
		const body = {};
		const initialName = trim(initialFormData?.name || "");
		const initialNote = trim(initialFormData?.note || "");

		// Cambios básicos
		if (trimmedName !== initialName) body.name = trimmedName || null;
		if (isActive !== initialFormData?.isActive) body.isActive = isActive;
		if (trimmedNote !== initialNote) body.note = trimmedNote || null;

		// Cambios en stages
		if (formattedStages.length > 0) body.stages = formattedStages;
		if (deletedStageIds.length > 0) body.stagesD = deletedStageIds;

		// Cambios en tiempos
		if (botWarningValue !== numOrNull(initialFormData?.botWarning)) body.botWarning = botWarningValue;
		if (botTimeoutValue !== numOrNull(initialFormData?.botTimeout)) body.botTimeout = botTimeoutValue;

		// Cambios en timeout action
		const hasActionChanged = botTimeoutAction !== (initialFormData?.botTimeoutAction || "");
		const origFieldValues =
			tableValue?.botTimeoutAction?.fields?.reduce((acc, field) => {
				acc[field.name] = field.value;
				return acc;
			}, {}) || {};
		const hasFieldValuesChanged = !isEqual(timeoutActionValues, origFieldValues);

		if (hasActionChanged || hasFieldValuesChanged) {
			if (botTimeoutAction) {
				const selectedAction = find(actions, { id: botTimeoutAction });
				body.botTimeoutAction = selectedAction
					? {
							...selectedAction,
							fields:
								selectedAction.fields?.map((field) => {
									let value = timeoutActionValues[field.name] ?? field.value;
									// Si es un campo de tipo file y está vacío, convertir a null
									if (field.type === "file" && Array.isArray(value) && value.length === 0) {
										value = null;
									}
									return {
										...field,
										value,
									};
								}) || [],
					  }
					: null;
			} else {
				body.botTimeoutAction = null;
			}
		}

		// Enviar cambios
		const dataEdit = await dispatch(
			genericAction({
				method: "patch",
				route: `bots/${tableValue.id}`,
				body,
			})
		);

		if (dataEdit) {
			apiData("Edit");
			setInitialFormData({
				name,
				isActive,
				note,
				stages,
				botTimeout: botTimeoutValue,
				botWarning: botWarningValue,
				botTimeoutAction,
			});
		}

		setErrors({
			name: "",
			isActive: "",
			botWarning: "",
			botTimeout: "",
			stagesError: [],
			optionError: [],
			botTimeoutAction: {},
		});
	};

	const cancelModal = () => {
		onClose();
	};
	const modalOpen = () => {
		const normalizeStages = (stageList) => {
			return map(stageList, (stage) => {
				return {
					name: stage.name,
					text: stage.text,
					type: stage.type,
					uri: stage.uri,
					isActive: stage.isActive ?? true,
					buttonLabel: stage.buttonLabel ?? "",
					buttonUri: stage.buttonUri ?? "",
					header: stage.header ?? "",
					footer: stage.footer ?? "",
					options: ["buttonUri", "buttonPhone"].includes(stage.type)
						? []
						: map(stage.options || [], (opt) => {
								return {
									name: opt.label || opt.name,
									isActive: opt.isActive ?? true,
									action:
										typeof opt.action === "string"
											? [
													{
														name: opt.action,
														type: opt.actionType || "message",
														value: opt.stageId || opt.actionValue || null,
													},
											  ]
											: Array.isArray(opt.action)
											? map(opt.action, (a) => {
													return {
														name: a.name,
														type: a.type,
														value: a.value ?? null,
													};
											  })
											: [],
									stageId: opt.stageId || null,
								};
						  }),
				};
			});
		};

		const currentData = {
			name,
			note,
			stages: normalizeStages(stages),
		};

		const initialData = {
			name: initialFormData.name,
			note: initialFormData.note,
			stages: normalizeStages(initialFormData.stages),
		};

		if (isEqual(currentData, initialData)) {
			cancelModal();
		} else {
			setOpen((prev) => {
				return !prev;
			});
		}
	};

	const handleStateHeader = (states) => {
		setStateHeader(states);
	};

	const handlePrint = () => {
		setPrintAction(true);
	};

	const handlePrintRetry = () => {
		setPrintAction(false);
	};

	const updateStageImage = (stageId, imageArray) => {
		setStages((prevStages) => {
			return map(prevStages, (stage) => {
				return stage.id === stageId ? { ...stage, image: imageArray } : stage;
			});
		});
	};

	const formatDropdownOptions = (inputOptions) => {
		return (inputOptions || []).map((opt, idx) => {
			return {
				key: `opt-${idx}`,
				text: opt,
				value: opt,
			};
		});
	};

	const formatGroupOptions = (groupsList) => {
		return groupsList.map((group, idx) => {
			return {
				key: `group-${idx}`,
				text: group.name || group.groupName,
				value: group.id || group.groupId || group.name || group.groupName,
			};
		});
	};

	// Detecta si la URI corresponde a una imagen
	const isImageUri = (uri) => {
		return typeof uri === "string" && (/^data:image\//.test(uri) || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(uri));
	};

	// Maneja subida de documentos (PDF, MP3, MP4, etc.)
	const handleDocumentChange = useCallback((val, fieldFiles, handleFieldChange) => {
		const currentImages = isArray(fieldFiles) ? filter(fieldFiles, isImageUri) : [];
		if (val) {
			handleFieldChange(concat(currentImages, val));
		} else {
			handleFieldChange(currentImages.length > 0 ? currentImages : null);
		}
	}, []);

	// Maneja cambio de imágenes desde FileManager
	const handleImageChange = useCallback((nextImages, handleFieldChange) => {
		const onlyImages = isArray(nextImages) ? filter(nextImages, isImageUri) : [];
		handleFieldChange(onlyImages.length > 0 ? onlyImages : null);
	}, []);

	// Combina imágenes y documentos en el mismo campo (si ya existían ambos) - REMOVIDO: no se usa más
	// const handleImageAndFileChange = useCallback((nextImages, fieldFiles, handleFieldChange) => {
	// 	const onlyImages = isArray(nextImages) ? filter(nextImages, isImageUri) : [];
	// 	const currentFiles = isArray(fieldFiles)
	// 		? filter(fieldFiles, (f) => {
	// 				return !isImageUri(f);
	// 		  })
	// 		: [];
	// 	const combined = concat(onlyImages, currentFiles);
	// 	handleFieldChange(combined.length > 0 ? combined : null);
	// }, []);

	// Función para procesar archivos de un campo file
	const processFileField = useCallback((currentValue) => {
		// Si currentValue es null, tratarlo como array vacío para la UI
		const normalizedValue = currentValue === null ? [] : isArray(currentValue) ? currentValue : currentValue ? [currentValue] : [];
		const imageUris = normalizedValue.filter(isImageUri);
		const fileUris = normalizedValue.filter((f) => {
			return !isImageUri(f);
		});
		const hasImages = imageUris.length > 0;
		const hasFile = fileUris.length > 0;

		return {
			normalizedValue,
			imageUris,
			fileUris,
			hasImages,
			hasFile,
		};
	}, []);

	const handleTimeoutActionChange = useCallback(
		(value) => {
			setBotTimeoutAction(value);
			setTimeoutActionValues({});
			const selectedAction = find(timeoutActions, { value });
			if (selectedAction) {
				const actionWithFields = find(actions, { id: value });
				setSelectedTimeoutActionFields(actionWithFields?.fields || []);
			} else {
				setSelectedTimeoutActionFields([]);
			}
		},
		[actions, timeoutActions]
	);

	return (
		<>
			<IKModal
				title={modalOption === "Add" ? "Agregar ChatBots" : `ChatBot: ${get(tableValue, "name", "")}`}
				id={String(modalOption === "Edit" && tableValue?.id ? tableValue.id : "")}
				mode={modalOption}
				setMode={setModalOption}
				onClose={modalOpen}
				onSuccess={handlerSaved}
				printAction={modalOption !== "Add" && stateHeader === "stages" ? handlePrint : null}
				size={modalCrud === "Add" ? "tiny" : "large"}
				className={modalCrud !== "Add" ? styles.modalContentEditWatch : styles.modalContent}
				fullHeight={modalCrud}>
				<div className={stateHeader === "infobasic" ? styles.containerBasic : styles.mainContainer}>
					<div className={modalOption === "Add" || stateHeader === "infobasic" ? styles.contentFullWidth : styles.content}>
						{modalOption !== "Add" && (
							<div className={styles.divHeaderNav}>
								<Button
									className={stateHeader === "infobasic" ? `${styles.navButton} ${styles.active}` : styles.navButton}
									onClick={() => {
										handleStateHeader("infobasic");
									}}>
									Información Básica
								</Button>

								<Button
									className={stateHeader === "stages" ? `${styles.navButton} ${styles.active}` : styles.navButton}
									onClick={() => {
										handleStateHeader("stages");
									}}>
									Menú/Opciones ({stages.length})
								</Button>
							</div>
						)}
						<Grid className={styles.grid}>
							<Grid.Row className={styles.grid}>
								{stateHeader === "infobasic" && (
									<>
										<Grid.Column width={modalOption !== "Add" ? 10 : 16} className={styles.grid2}>
											<div className={styles.classContainerHeader} ref={stateHeader === "infobasic" ? printContentRef : null}>
												<div className={styles.subContainerHeader}>
													<h2 className={styles.subContainerTitle}>
														<Icon name="info" className={styles.icon} />
														Información Básica
													</h2>
												</div>
												<div className={styles.containerBody}>
													<div className={styles.containerNameActive}>
														<div className={styles.fieldName}>
															<Text className={styles.text}>
																Nombre:<span className={styles.requiredAsterisk}>*</span>
															</Text>
															<Input
																icon="user"
																name="name"
																placeholder="Nombre del chatBot"
																autoComplete="off"
																disabled={modalOption === "Watch"}
																value={name}
																onChange={(e) => {
																	setName(e.target.value);
																}}
																error={
																	!isEmpty(errors.name) && {
																		content: errors.name,
																		pointing: "above",
																		className: styles.inputError,
																	}
																}
															/>
														</div>
														<div className={styles.fieldActive}>
															<Text className={styles.Active}>Activo</Text>
															<Checkbox
																toggle
																label={isActive ? "Sí" : "No"}
																checked={isActive}
																onChange={(e, { checked }) => {
																	setIsActive(checked);
																}}
																disabled={modalOption === "Watch"}
																className={styles.checkboxActive}
															/>
														</div>
													</div>
													<div className={styles.containerTime}>
														<div className={styles.timeWar}>
															<Text className={styles.text}>
																Tiempo de Aviso:<span className={styles.requiredAsterisk}>*</span>
															</Text>
															<Input
																type="number"
																min="0"
																icon="time"
																name="botWarning"
																placeholder="Minutos (15)"
																autoComplete="off"
																disabled={modalOption === "Watch"}
																value={String(botWarning)}
																onChange={(e) => {
																	return setBotWarning(e.target.value);
																}}
																error={
																	!isEmpty(errors.botWarning) && {
																		content: errors.botWarning,
																		pointing: "above",
																		className: styles.inputError,
																	}
																}
															/>
														</div>

														<div className={styles.timeWar}>
															<Text className={styles.text}>
																Tiempo de Timeout:<span className={styles.requiredAsterisk}>*</span>
															</Text>
															<Input
																type="number"
																min="0"
																icon="time"
																name="botTimeout"
																placeholder="Minutos (15)"
																autoComplete="off"
																disabled={modalOption === "Watch"}
																value={String(botTimeout)}
																onChange={(e) => {
																	return setBotTimeout(e.target.value);
																}}
																error={
																	!isEmpty(errors.botTimeout) && {
																		content: errors.botTimeout,
																		pointing: "above",
																		className: styles.inputError,
																	}
																}
															/>
														</div>

														{/* Mostrar campos de la acción seleccionada */}
														<div className={styles.timeWarDrop}>
															<fieldset className={styles.actionFieldsContainer}>
																<legend className={styles.actionFieldsTitle}>
																	<span className={styles.actionFieldsLegend}>Configuración</span>
																</legend>

																<div className={styles.timeWar}>
																	<Text className={styles.text}>
																		Acción al Timeout:
																		<span className={styles.requiredAsterisk}>*</span>
																	</Text>

																	<Dropdown
																		selection
																		name="botTimeoutAction"
																		placeholder="Seleccione una acción"
																		disabled={modalOption === "Watch"}
																		value={botTimeoutAction}
																		options={timeoutActions}
																		onChange={(e, { value }) => {
																			handleTimeoutActionChange(value);
																		}}
																		className={styles.dropdown}
																		search
																		searchInput={{ autoFocus: false }}
																	/>
																	{!isEmpty(errors.botTimeoutAction) && (
																		<Label basic color="red" pointing className={styles.errorLabel}>
																			{errors.botTimeoutAction}
																		</Label>
																	)}
																</div>
																<Divider className={styles.timeoutDivider} />

																{/* Renderizar campos agrupados por tipo */}
																{["string", "number", "date", "dropdown", "group", "file"].map((fieldType) => {
																	const fieldsOfType = selectedTimeoutActionFields.filter((field) => {
																		return field.type === fieldType;
																	});

																	if (fieldsOfType.length === 0) return null;

																	return (
																		<div key={fieldType} className={styles.fieldOptionAuto}>
																			{fieldsOfType.map((field, index) => {
																				const fieldKey = field.name || field.label?.toLowerCase().replace(/\s+/g, "_");
																				const currentValue = timeoutActionValues[fieldKey] || "";
																				const isDisabled = modalOption === "Watch";

																				// Verificar si el campo "type" tiene valor "Text" para ocultar el adjunto
																				const typeFieldValue = timeoutActionValues.type;
																				const shouldHideAttachment = field.type === "file" && typeFieldValue === "Text";

																				const handleFieldChange = (value) => {
																					setTimeoutActionValues((prev) => {
																						const newValues = {
																							...prev,
																							[fieldKey]: value,
																						};
																						if (field.name === "type" && value === "Text") {
																							newValues.uri = null;
																						}
																						return newValues;
																					});
																				};

																				// Si el tipo es "Text" y es un campo de archivo, no renderizar nada
																				if (shouldHideAttachment) {
																					return null;
																				}

																				return (
																					<div key={field.id || index} className={styles.fieldOption}>
																						<Text className={styles.text}>{field.label}:</Text>

																						{field.type === "string" && (
																							<TextAreaInput
																								placeholder={`Ingresa texto para ${field.label}`}
																								value={currentValue}
																								disabled={isDisabled}
																								onChange={(e) => {
																									handleFieldChange(e.target.value);
																								}}
																								maxRows={4}
																							/>
																						)}

																						{field.type === "number" && (
																							<Input
																								type="number"
																								min="0"
																								icon="calculator"
																								placeholder={`Valor numérico para "${field.label}"`}
																								value={currentValue}
																								disabled={isDisabled}
																								onChange={(e, { value }) => {
																									handleFieldChange(value);
																								}}
																							/>
																						)}

																						{field.type === "date" && (
																							<Input
																								type="date"
																								icon="calendar"
																								placeholder={`Selecciona fecha para ${field.label}`}
																								value={currentValue}
																								disabled={isDisabled}
																								onChange={(e, { value }) => {
																									handleFieldChange(value);
																								}}
																							/>
																						)}

																						{field.type === "dropdown" && (
																							<Dropdown
																								placeholder={`Selecciona ${field.label}`}
																								selection
																								clearable
																								disabled={isDisabled}
																								value={currentValue}
																								options={formatDropdownOptions(field.inputOptions)}
																								onChange={(e, { value }) => {
																									handleFieldChange(value);
																								}}
																							/>
																						)}

																						{field.type === "group" && (
																							<Dropdown
																								placeholder={`Selecciona ${field.label}`}
																								selection
																								clearable
																								disabled={isDisabled}
																								value={currentValue}
																								options={formatGroupOptions(groups)}
																								onChange={(e, { value }) => {
																									handleFieldChange(value);
																								}}
																								upward
																							/>
																						)}

																						{field.type === "file" &&
																							(() => {
																								// Verificar si el campo "type" tiene valor "Text" para ocultar el adjunto
																								const currentTypeFieldValue = timeoutActionValues.type;
																								const currentShouldHideAttachment = currentTypeFieldValue === "Text";

																								// Si el tipo es "Text", no mostrar el campo de adjunto (incluyendo el label)
																								if (currentShouldHideAttachment) {
																									return null;
																								}

																								const { normalizedValue, imageUris, fileUris } = processFileField(currentValue);

																								// Determinar qué controles mostrar basado en el tipo
																								const isImageType = currentTypeFieldValue === "Image";
																								const isAudioType = currentTypeFieldValue === "Audio";
																								const isVideoType = currentTypeFieldValue === "Video";
																								const isDocumentType = currentTypeFieldValue === "Document";

																								return (
																									<div className={styles.fileManager}>
																										<div className={styles.containerImagenss}>
																											{/* Mostrar FileManager solo para tipo Image */}
																											{isImageType && (
																												<div className={styles.classImage}>
																													<FileManager
																														images={imageUris}
																														setImages={(nextImages) => {
																															return handleImageChange(nextImages, handleFieldChange);
																														}}
																														disabled={isDisabled}
																													/>
																												</div>
																											)}

																											{/* Mostrar UploadFile para otros tipos */}
																											{(isAudioType || isVideoType || isDocumentType) && (
																												<div className={styles.upload}>
																													<UploadFile
																														document={fileUris[0] || null}
																														setDocument={(val) => {
																															return handleDocumentChange(val, normalizedValue, handleFieldChange);
																														}}
																														accept={
																															isAudioType
																																? ".mp3,.wav,.ogg"
																																: isVideoType
																																? ".mp4,.avi,.mov"
																																: ".pdf,.doc,.docx"
																														}
																														disabled={isDisabled}
																													/>
																												</div>
																											)}

																											{/* Mostrar ambos controles si no hay tipo específico */}
																											{!isImageType && !isAudioType && !isVideoType && !isDocumentType && (
																												<>
																													<div className={styles.classImage}>
																														<FileManager
																															images={imageUris}
																															setImages={(nextImages) => {
																																return handleImageChange(nextImages, handleFieldChange);
																															}}
																															disabled={isDisabled}
																														/>
																													</div>
																													<div className={styles.upload}>
																														<UploadFile
																															document={null}
																															setDocument={(val) => {
																																if (val) handleFieldChange([val]);
																																else handleFieldChange([]);
																															}}
																															accept=".pdf,.mp4,.mp3"
																															disabled={isDisabled}
																														/>
																													</div>
																												</>
																											)}
																										</div>
																									</div>
																								);
																							})()}
																					</div>
																				);
																			})}
																		</div>
																	);
																})}
															</fieldset>
														</div>
													</div>

													<div>
														<TextAreaInput
															label="Descripción:"
															name="note"
															value={note}
															placeholder="Descripción del chatbot..."
															onChange={(e) => {
																return setNote(e.target.value);
															}}
															disabled={modalOption === "Watch"}
															error={!isEmpty(errors.decription) ? errors.decription : ""}
															minRows={1}
															maxRows={6}
														/>
													</div>
												</div>
											</div>
										</Grid.Column>
										{modalOption !== "Add" && (
											<Grid.Column width={6} className={styles.grid2}>
												<div className={styles.classContainerHeader}>
													<div className={styles.subContainerHeader}>
														<h2 className={styles.subContainerTitle}>
															<Icon name="setting" className={styles.icon} />
															Configuración
														</h2>
													</div>
													<div className={styles.containerBody}>
														<div className={styles.configGuideBlock}>
															<div className={styles.configGuideSection}>
																<h3>Reply Buttons (Botones)</h3>
																<ul>
																	<li>Máximo: 3 botones</li>
																	<li>Texto del botón (title): 20 caracteres máximo</li>
																	<li>ID del botón: 256 caracteres máximo</li>
																	<li>Header de texto: 60 caracteres máximo</li>
																	<li>Footer: 60 caracteres máximo</li>
																	<li>Body: 1024 caracteres máximo</li>
																</ul>
															</div>
															<div className={styles.configGuideSection}>
																<h3>List Messages (Listas)</h3>
																<ul>
																	<li>Máximo: 10 opciones (rows) en total</li>
																	<li>Máximo: 10 secciones</li>
																	<li>Texto del botón que abre la lista: 20 caracteres máximo</li>
																	<li>Título de sección: 24 caracteres máximo</li>
																	<li>Título de fila (row): 24 caracteres máximo</li>
																	<li>Descripción de fila: 72 caracteres máximo</li>
																	<li>Header de texto: 60 caracteres máximo</li>
																	<li>Footer: 60 caracteres máximo</li>
																	<li>Body: 1024 caracteres máximo</li>
																</ul>
															</div>
														</div>
													</div>
												</div>
											</Grid.Column>
										)}
									</>
								)}

								{stateHeader === "stages" && (
									<Grid.Column width={16} className={styles.grid}>
										<div className={styles.infoBasicWrapper}>
											<div className={styles.classContainerHeaderMenu} ref={printContentRef}>
												<div className={styles.subContainerHeader}>
													<h2 className={styles.subContainerTitle}>
														<Icon name="android" className={styles.icon} /> Menú / Opciones
													</h2>
													<div className={styles.expandAllContainer}>
														<span className={styles.expandText}>{isExp ? "Ocultar Todas" : "Expandir Todas"}</span>
														<Icon name={isExp ? "angle up" : "angle down"} onClick={handlerExpandAll} className={styles.expandIcon} />
													</div>
												</div>

												<div className={styles.containerBody}>
													<StageOptionsComponent
														stages={stages}
														setStages={setStages}
														errors={errors}
														images={images}
														setImages={setImages}
														updateStageImage={updateStageImage}
														modalOption={modalOption}
														actionsList={actions}
														warningMinutes={Number(botWarning) || 0}
														timeoutMinutes={Number(botTimeout) || 0}
														document={document}
														setDocument={setDocument}
													/>
												</div>
											</div>
										</div>
									</Grid.Column>
								)}
							</Grid.Row>
						</Grid>
					</div>
					{modalOption !== "Add" && stateHeader !== "infobasic" && (
						<div className={styles.containerHistory}>
							<MenuHistory
								stages={stages}
								expandedMenus={expandedMenus}
								setExpandedMenus={setExpandedMenus}
								expandedOptions={expandedOptions}
								setExpandedOptions={setExpandedOptions}
								actionCatalog={actions}
							/>
						</div>
					)}
				</div>
			</IKModal>
			<div className={styles.hidden}>
				<div ref={printAreaRef}>
					<div className="printHistoryLarge printHistoryContainerLarge">
						<MenuHistory
							stages={stages}
							expandedMenus={expandedMenus}
							setExpandedMenus={setExpandedMenus}
							expandedOptions={expandedOptions}
							setExpandedOptions={setExpandedOptions}
						/>
					</div>
				</div>
			</div>
			<PrintRender
				renderData={printAreaRef}
				printHeader={{
					company: companyName || "",
					title: `ChatBot: ${name}`,
					user: userName || "",
				}}
				printAction={printAction}
				printActionRetry={handlePrintRetry}
				hiddenButon
				hideClientInfo
			/>
			{open && (
				<ConfirmModal
					visible
					header="Salir del formulario"
					content="Está seguro que desea salir del formulario"
					onConfirm={cancelModal}
					onDismiss={modalOpen}
					centerModal
				/>
			)}
		</>
	);
};

function mapStateToProps({ user, company }) {
	return {
		companies: [],
		companyName: company.name,
		userName: user.name,
		groups: company?.groups || user?.groups || [],
	};
}

ChatBotCrud.propTypes = {
	tableValue: PropTypes.object,
	onClose: PropTypes.func,
	apiData: PropTypes.func,
	dispatch: PropTypes.func,
	modalCrud: PropTypes.string,
	companyName: PropTypes.string,
	userName: PropTypes.string,
	onAddSuccess: PropTypes.func,
	groups: PropTypes.array,
};

export default connect(mapStateToProps)(ChatBotCrud);
